/** Here is the typical component from react being rendered for the corresponding form*/
import React from "react";
import _ from "lodash";
import EntityForm from "../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import {
  CODE_TYPE,
  DEVICE_TYPE,
  PAYMENT_TYPES,
  PAYMENT_TYPES_NAMES,
  PNP_SDK_PAYMENTS_METHODS,
  RESTRICTION_CONFIGURATION_TYPE,
  RESTRICTION_TYPE,
  RESTRICTION_VALUE_TYPE,
  WINDOW_TYPE,
} from "../../Common/constants";
import { API_PAYMENTS_URL } from "../../request";
import { resolveAdyenHmacKeyPrevious } from "./utils";
import "./customStyles.css";

function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig, locale }) {
  let formValues = entityToFormValuesMapper({
    entity,
    fields,
    locales,
    translationConfig,
  });

  const minReleaseDuration = entity.minRelease ? entity.minRelease.duration : undefined;
  const maxReleaseDuration = entity.maxRelease ? entity.maxRelease.duration : undefined;

  // TODO: generify handling of restrictions for every bookable / restrictable
  if (!_.isEmpty(entity.restrictions)) {
    formValues[RESTRICTION_TYPE.DEVICE_TYPE] =
      entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE] &&
      entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE].restrictionValue &&
      entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE].restrictionValue.value;

    formValues[RESTRICTION_TYPE.CODE] =
      entity.restrictions[RESTRICTION_TYPE.CODE] &&
      entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue &&
      entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value &&
      entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value.join(",");

    formValues.codeType = formValues[RESTRICTION_TYPE.CODE]
      ? CODE_TYPE.UNIVERSAL_CODE
      : CODE_TYPE.NO_CODE;
  } else {
    // selected by default
    formValues[RESTRICTION_TYPE.DEVICE_TYPE] = [
      DEVICE_TYPE.TABLET,
      DEVICE_TYPE.DESKTOP,
      DEVICE_TYPE.MOBILE,
    ];
  }

  const title = PAYMENT_TYPES_NAMES[locale][entity.type];
  return {
    ...formValues,
    ...entity.properties,
    septeoClientId: entity.properties?.clientId,
    validationRequiredForZeroAmount: true,
    minDuration: minReleaseDuration,
    maxDuration: maxReleaseDuration,
    title,
    installmentsIsActive:
      entity.properties?.installmentsIsActive === "true" || formValues.installmentsIsActive,
  };
}

class PaymentTypesForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getFields(fields) {
    return fields && fields.form;
  }

  handleSubmit({ formValues, entity }) {
    const { config } = this.props;

    const properties = {};

    const propertyList = [
      "Ds_Merchant_Amount",
      "Ds_Merchant_Currency",
      "Ds_Merchant_MerchantCode",
      "Ds_Merchant_MerchantURL",
      "Ds_Merchant_Order",
      "Ds_Merchant_PayMethods",
      "Ds_Merchant_Terminal",
      "Ds_Merchant_TransactionType",
      "url",
      "clave256",
      "allowTokenize",
      "secret",
      "rebatePassword",
      "pnpClient",
      "pnpGateway",
      "key",
      "roomLockHours",
      "deferred",
      "automaticCancellation",
      "merchantId",
      "merchantProductId",
      "merchantPassword",
      "clientId",
      "service",
      "apiKey",
      "signature",
      "template",
      "chargeDeposit",
      "multiAcquirers",
      "paymentsMethods",
      "merchantAccount",
      "publicApiKey",
      "installmentsIsActive",
      "installmentsValues",
      "prefix",
      "hmacKey",
      "signaturePassword",
    ];

    for (let property in formValues) {
      if (
        formValues.hasOwnProperty(property) &&
        formValues[property] &&
        propertyList.includes(property)
      ) {
        properties[property] = formValues[property];
      }
    }

    if (formValues.type === PAYMENT_TYPES.POS) {
      properties.tipo = "sermepa";
    }

    if (formValues.type === PAYMENT_TYPES.POS) {
      properties.Ds_Merchant_MerchantURL = `${API_PAYMENTS_URL}/tpv/${entity.propertyTicker}/notification`;
    }
    if (formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2) {
      properties.secure = false;
      properties.multiAcquirers = formValues.multiAcquirers === "true" ? "true" : "false";
      properties.validationRequiredForZeroAmount = formValues.validationRequiredForZeroAmount
        ? "true"
        : "false";
    }
    if (formValues.type === PAYMENT_TYPES.CHECKOUT_PNP) {
      properties.secure = false;
      if (Array.isArray(formValues.paymentMethods)) {
        properties.paymentMethods = formValues.paymentMethods.map(it => it.value).join(",");
      } else {
        properties.paymentMethods = formValues.paymentMethods;
      }
    }
    if (formValues.type === PAYMENT_TYPES.PNP_SDK) {
      properties.secure = false;

      const componentType =
        typeof formValues.componentType === "object" && formValues.componentType !== null
          ? formValues.componentType.value
          : formValues.componentType;
      const isExpress = componentType === "express";

      if (isExpress) {
        properties.componentType = "express";
        properties.gatewayMerchantId = formValues.gatewayMerchantId || undefined;
        properties.googlePayMerchantId = formValues.googlePayMerchantId || undefined;
      }
      // Standard modality: omit componentType entirely. The booking engine treats an
      // absent (or any non-"express") value as standard, and this matches the contract
      // fixture agreed with the engine team.

      const allowedMethods = isExpress
        ? [PNP_SDK_PAYMENTS_METHODS.GOOGLE_PAY, PNP_SDK_PAYMENTS_METHODS.APPLE_PAY]
        : Object.values(PNP_SDK_PAYMENTS_METHODS);
      const selectedMethods = Array.isArray(formValues.paymentMethods)
        ? formValues.paymentMethods.map(it => (typeof it === "object" ? it.value : it))
        : (formValues.paymentMethods || "").split(",").filter(Boolean);

      // Switching standard -> express leaves a stale "paypal" in the multi-select value.
      properties.paymentMethods = selectedMethods
        .filter(it => allowedMethods.includes(it))
        .join(",");
    }
    if (formValues.type === PAYMENT_TYPES.PAYCOMET) {
      properties.secure = false;
      properties.terminalId = formValues.terminalId;
      if (Array.isArray(formValues.paymentMethods)) {
        properties.paymentMethods = formValues.paymentMethods.map(it => it.value).join(",");
      } else {
        properties.paymentMethods = formValues.paymentMethods;
      }
    }
    if (formValues.type === PAYMENT_TYPES.PAYCOMET_IVR) {
      properties.secure = false;
      properties.terminalId = formValues.terminalId;
      properties.ivrProviderId = formValues.ivrProviderId;
      properties.ivrSessionTimeout = formValues.ivrSessionTimeout;
    }
    if (formValues.type === PAYMENT_TYPES.ADDON_PAYMENTS) {
      properties.secure = false;
      properties.merchantId = formValues.merchantId;
      properties.merchantProductId = formValues.merchantProductId;
      properties.publicApiKey = formValues.publicApiKey;
      properties.merchantPassword = formValues.merchantPassword;
      if (Array.isArray(formValues.paymentMethods)) {
        properties.paymentMethods = formValues.paymentMethods.map(it => it.value).join(",");
      } else {
        properties.paymentMethods = formValues.paymentMethods;
      }
    }
    if (formValues.type === PAYMENT_TYPES.ADYEN) {
      properties.secure = false;
      properties.merchantAccount = formValues.merchantAccount;
      properties.installmentsIsActive = formValues.installmentsIsActive;
      properties.prefix = formValues.prefix;
      properties.installmentsValues = formValues.installmentsIsActive
        ? formValues.installmentsValues
        : undefined;
      properties.hmacKeyPrevious = resolveAdyenHmacKeyPrevious({
        previousProperties: entity.properties,
        formValues,
      });
      if (Array.isArray(formValues.paymentMethods)) {
        properties.paymentMethods = formValues.paymentMethods.map(it => it.value).join(",");
      } else {
        properties.paymentMethods = formValues.paymentMethods;
      }
    }
    if (formValues.type === PAYMENT_TYPES.DEFERRED) {
      properties.deferred = true;
      properties.automaticCancellation = true;
    }
    if (formValues.type === PAYMENT_TYPES.WITHOUT_CARD) {
      properties.roomLockHours = -1;
      properties.automaticCancellation = false;
    }
    if (formValues.type === PAYMENT_TYPES.STRIPE) {
      properties.accountType = formValues.accountType;
      properties.accountId = formValues.accountId || undefined;
      properties.accountLink = formValues.accountLink || undefined;
      properties.accountLinkExpiresAt = formValues.accountLinkExpiresAt || undefined;
      if (typeof formValues.componentType === "object") {
        properties.componentType = formValues.componentType.value;
      } else {
        properties.componentType = formValues.componentType;
      }
    }
    if (formValues.type === PAYMENT_TYPES.FREEDOM_PAY) {
      properties.esKey = formValues.esKey;
      properties.storeId = formValues.storeId;
      properties.terminalId = formValues.terminalId;
      if (Array.isArray(formValues.paymentMethods)) {
        properties.paymentMethods = formValues.paymentMethods.map(it => it.value).join(",");
      } else {
        properties.paymentMethods = formValues.paymentMethods;
      }
    }
    if (formValues.type === PAYMENT_TYPES.NEMURU) {
      properties.agentId = formValues.agentId;
    }
    if (formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS) {
      properties.receiverHash = formValues.receiverHash;
      properties.clientKey = formValues.clientKey;
      properties.clientId = formValues.septeoClientId || undefined;
      properties.clientSecret = formValues.clientSecret || undefined;
      properties.notificationSecret = formValues.notificationSecret;
      if (typeof formValues.componentType === "object") {
        properties.componentType = formValues.componentType.value;
      } else {
        properties.componentType = formValues.componentType;
      }
    }
    if (formValues.type === PAYMENT_TYPES.SEPTEO_VAULT) {
      properties.receiverHash = formValues.receiverHash;
      properties.clientKey = formValues.clientKey;
    }
    if (formValues.type === PAYMENT_TYPES.SIPAY) {
      properties.clientKey = formValues.clientKey;
      properties.clientSecret = formValues.clientSecret;
      properties.resource = formValues.resource;
      properties.googlePayMerchantId = formValues.googlePayMerchantId;
      if (typeof formValues.componentType === "object") {
        properties.componentType = formValues.componentType.value;
      } else {
        properties.componentType = formValues.componentType;
      }
    }
    const maxRelease = {
      duration: formValues.maxDuration,
      chronoUnit: "HOURS",
    };
    const minRelease = {
      duration: formValues.minDuration,
      chronoUnit: "HOURS",
    };
    if (
      config.hasOwnProperty("translation") &&
      config.translation &&
      config.translation.hasOwnProperty("fieldName")
    ) {
      const translationField = config.translation.fieldName;
      const translationValues =
        (formValues.hasOwnProperty(translationField) && formValues[translationField]) || [];
      entity[config.translation.entityProperty] = translationValues.map(translation => {
        return {
          language: translation.locale,
          title: translation.title ? translation.title : "title",
          description: translation.description,
          descriptionWithDeposit: translation.descriptionWithDeposit,
          descriptionWithoutDeposit: translation.descriptionWithoutDeposit,
        };
      });
    }

    const restrictions = {};

    // TODO: generify handling of restrictions for every bookable / restrictable
    if (formValues[RESTRICTION_TYPE.DEVICE_TYPE]) {
      const hasDevices =
        formValues[RESTRICTION_TYPE.DEVICE_TYPE] &&
        formValues[RESTRICTION_TYPE.DEVICE_TYPE].length > 0;

      restrictions[RESTRICTION_TYPE.DEVICE_TYPE] = {
        restrictionValue: {
          value: formValues[RESTRICTION_TYPE.DEVICE_TYPE],
          valueType: hasDevices
            ? RESTRICTION_VALUE_TYPE.DEVICE_TYPE_SET
            : RESTRICTION_VALUE_TYPE.NONE,
        },
        restrictionType: RESTRICTION_TYPE.DEVICE_TYPE,
        configurationType: hasDevices
          ? RESTRICTION_CONFIGURATION_TYPE.CONSTANT
          : RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT,
        windowType: WINDOW_TYPE.BOOKING,
      };
    }

    if (formValues[RESTRICTION_TYPE.CODE] && formValues.codeType === CODE_TYPE.UNIVERSAL_CODE) {
      restrictions[RESTRICTION_TYPE.CODE] = {
        restrictionValue: {
          value: formValues[RESTRICTION_TYPE.CODE].split(","),

          valueType: "CODE_SET",
        },
        restrictionType: "CODE",
        configurationType: "CONSTANT",
        windowType: "BOOKING",
      };
    }

    const entityToSend = {
      active: entity.active,
      creationDate: entity.creationDate,
      description: entity.description,
      id: entity.id,
      lastUpdate: entity.lastUpdate,
      order: entity.order,
      paymentInAdvance: entity.paymentInAdvance,
      paymentPercentage: entity.paymentPercentage,
      propertyTicker: entity.propertyTicker,
      ticker: entity.ticker,
      title: entity.title,
      translations: entity.translations,
      type: entity.type,
      properties,
      maxRelease,
      minRelease,
      restrictions,
    };

    this.props.onSubmitEntity({
      entity: entityToSend,
    });
  }

  /* Handle Errors */
  render() {
    const {
      name,
      validation,
      fields,
      renderers,
      translation,
      entities,
      locale,
    } = this.props.config;
    const { locales, entity, entityError, entityResult, loading, getListPath } = this.props;
    const { getFields, handleSubmit } = this;
    return (
      <div className={"wbkv9-payment-form"}>
        <EntityForm
          loading={loading}
          name={name}
          entity={entity}
          entities={entities}
          validation={validation}
          renderers={renderers}
          fields={getFields(fields)}
          locales={locales}
          translation={translation}
          onSubmit={handleSubmit}
          entityError={entityError}
          entityResult={entityResult}
          getListPath={getListPath}
          entityToFormValuesMapper={customEntityToFormValuesMapper}
          validateVisibleOnly
          locale={locale}
        />
      </div>
    );
  }
}

PaymentTypesForm.propTypes = {};

PaymentTypesForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
};

export default PaymentTypesForm;
